import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../translations';
import { resolveBrowserTimeZone } from '../utils/timeZone';
import {
  type SidekickConfig,
  SidekickConfigContext,
} from './SidekickConfigContext';

/**
 * Props for {@link SidekickConfigProvider}. Both `timeZone` and
 * `locale` are optional; omitting either delegates to the browser
 * (`Intl.DateTimeFormat().resolvedOptions().timeZone`) or to
 * i18next's active language respectively.
 */
export type SidekickConfigProviderProps = {
  timeZone?: string;
  locale?: string;
  /**
   * Stable signed-in-user identifier used only to scope the
   * localStorage consent fallback key. See {@link SidekickConfig.userId}.
   */
  userId?: string;
  children: ReactNode;
};

/**
 * Provides Sidekick formatters and renderers with the consumer's
 * timezone and locale. Hosts that surface a user-profile timezone
 * (e.g. `fleet-management` via `userProfile.userPreferences`) wire
 * it in here; otherwise the provider falls back to the browser
 * timezone and the active i18next language.
 *
 * Consumers integrating via {@link SidekickLayoutShell} get this
 * provider mounted internally — pass `timeZone` / `locale` directly
 * on the shell instead of wrapping it yourself. Direct mounting of
 * this provider is only needed for Storybook stories, isolated
 * tests, or future surfaces that consume `useSidekickConfig` outside
 * the shell tree.
 *
 * @param props.timeZone - IANA timezone identifier (e.g.
 *   `America/New_York`). Defaults to the browser's resolved zone.
 * @param props.locale - BCP-47 locale tag (e.g. `en-US`). Defaults
 *   to `i18n.language` reported by the active i18next instance.
 */
export const SidekickConfigProvider = ({
  timeZone,
  locale,
  userId,
  children,
}: SidekickConfigProviderProps): JSX.Element => {
  const { i18n } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);

  const value = useMemo<SidekickConfig>(
    () => ({
      timeZone: timeZone ?? resolveBrowserTimeZone(),
      locale: locale ?? i18n.language ?? 'en-US',
      userId,
    }),
    [timeZone, locale, userId, i18n.language]
  );

  return (
    <SidekickConfigContext.Provider value={value}>
      {children}
    </SidekickConfigContext.Provider>
  );
};

export default SidekickConfigProvider;
